<template>
  <div class="action-manager">
    <div class="opportunity-status-manager">
      <BaseStatusMenu
        ref="opportunityActionManagerStatusMenu"
        :items="opportunityStatuses"
        :attach="`.opportunity-status-manager`"
        @change="(status) => onStatusChange(status)"
        showIcon
        searchAllowed
      />
    </div>
    <BaseRoundButton
      v-if="tenantHasChromeExtensionAccess()"
      @click.native="onDownloadDocuments()"
      :tooltip-text="downloadDocumentsTooltip"
      icon="file-input"
      is-white
      :disabled="!isExtensionCredentialsEntered"
    />
    <BaseRoundButton @click.native="onDelete()" :tooltip-text="$t('actions.delete')" icon="trash-2" is-red is-white />
    <ConfirmationDialog
      ref="confirmationDialog"
      :buttonText="$t('actions.delete')"
      @execute="executeDeleteOpportunities"
    >
      <template slot="content">
        <div>
          {{ $tc('page.opportunity.delete-opportunities-confirmation', this.opportunities.length) }}
          <div class="dialog__warning-notice">
            {{
              $tc('page.opportunity.delete-opportunity-warning', this.opportunities.length, {
                count: this.opportunities.length,
              })
            }}
          </div>
        </div>
      </template>
    </ConfirmationDialog>
    <ConfirmationDialog
      ref="downloadConfirmationDialog"
      :buttonText="
        downloadableOpportunities.length === 0
          ? $t('actions.got-it')
          : $t('aloha-extension.download-documents', { count: downloadableOpportunities.length })
      "
      @execute="executeDownloadDocuments"
    >
      <template slot="content">
        <span
          >{{
            $tc('aloha-extension.seao-opportunities-info', downloadableOpportunities.length, {
              count: downloadableOpportunities.length,
            })
          }}
        </span>
        <div
          v-if="nonDownloadableOpportunities.nonEligibleCount > 0"
          class="download-confirmation-dialog__warning-notice"
        >
          <div class="download-confirmation-dialog__warning-notice-header">
            <div class="download-confirmation-dialog__alert-icon">
              <BaseIcon name="alert-circle" />
            </div>
            <span>{{
              $tc('aloha-extension.warnings.non-eligible', nonDownloadableOpportunities.nonEligibleCount, {
                count: nonDownloadableOpportunities.nonEligibleCount,
              })
            }}</span>
          </div>
          <div
            v-for="item in nonDownloadableOpportunities.nonEligibleItems"
            :key="`non-eligible-${item.opportunity.id}`"
          >
            <div class="download-confirmation-dialog__warning-notice-opportunity">
              <PlatformIcon
                v-if="item.opportunity.tender && item.opportunity.tender.source"
                :platform="item.opportunity.tender.source.id"
              />
              <span>{{ item.opportunity.tender ? item.opportunity.tender.title : item.opportunity.name }}</span>
            </div>
            <div class="download-confirmation-dialog__warning-notice-text-warning">
              {{ $t(`aloha-extension.warnings.${item.reason}`) }}
            </div>
          </div>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog.vue';
import { tenantHasChromeExtensionAccess } from '@/util/module-access-util';
import BaseStatusMenu from '@/components/base/BaseStatusMenu.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import PlatformIcon from '@/components/PlatformIcon.vue';

export default {
  name: 'OpportunitiesActionsManager',
  components: {
    PlatformIcon,
    BaseIcon,
    BaseRoundButton,
    ConfirmationDialog,
    BaseStatusMenu,
  },
  props: {
    opportunities: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters('opportunityStatuses', ['opportunityStatuses']),
    ...mapGetters('chromeExtension', {
      isExtensionCredentialsEntered: 'isCredentialsEntered',
    }),
    downloadDocumentsTooltip() {
      if (!this.isExtensionCredentialsEntered) {
        return this.$t('aloha-extension.no-auth-infos');
      }
      return this.$t('aloha-extension.download-documents', { count: this.downloadableOpportunities.length });
    },
    downloadableOpportunities() {
      return this.opportunities.filter(
        (opportunity) =>
          opportunity.tender &&
          opportunity.tender.source &&
          opportunity.tender.source.name === 'SEAO' &&
          opportunity.tender.status === 'active',
      );
    },
    nonDownloadableOpportunities() {
      const nonActive = this.opportunities.filter(
        (opportunity) => !opportunity.tender || !opportunity.tender.status || opportunity.tender.status !== 'active',
      );
      const nonSeao = this.opportunities.filter(
        (opportunity) => opportunity.tender && opportunity.tender.source && opportunity.tender.source.name !== 'SEAO',
      );
      const externalSource = this.opportunities.filter(
        (opportunity) => !opportunity.tender || !opportunity.tender.source,
      );
      const merged = [
        ...nonActive.map((opportunity) => ({ opportunity, reason: 'non-eligible-status' })),
        ...nonSeao.map((opportunity) => ({ opportunity, reason: 'non-eligible-platform' })),
        ...externalSource.map((opportunity) => ({ opportunity, reason: 'external-source' })),
      ];
      const mergedDistinct = Array.from(new Map(merged.map((item) => [item.opportunity.id, item])).values());
      return {
        nonEligibleItems: mergedDistinct,
        nonEligibleCount: mergedDistinct.length,
      };
    },
  },
  methods: {
    tenantHasChromeExtensionAccess,
    ...mapActions('opportunities', ['deleteMany', 'updateOpportunitiesStatus']),
    onDelete() {
      this.$refs.confirmationDialog.openDialog();
    },
    executeDeleteOpportunities() {
      this.deleteMany(this.opportunities)
        .then(() => {
          this.$toast.success(this.$tc('toast.delete-opportunity', this.opportunities.length));
          this.$emit('refresh-opportunities');
        })
        .catch((error) => this.$toast.error(error));
    },
    onStatusChange({ id: newOpportunityStatusId, name: opportunityStatusName }) {
      const payload = {
        newOpportunityStatusId,
        filters: { opportunityIds: this.opportunities.map((o) => o.id) },
      };
      this.updateOpportunitiesStatus(payload)
        .then(() => {
          this.$toast.success(
            this.$tc('toast.update-opportunity-status', this.opportunities.length, {
              status: opportunityStatusName,
            }),
          );
          this.$emit('refresh-opportunities');
        })
        .catch((error) => this.$toast.error(error));
    },
    onDownloadDocuments() {
      this.$refs.downloadConfirmationDialog.openDialog();
    },
    executeDownloadDocuments() {
      this.downloadableOpportunities.forEach((opportunity) => {
        window.postMessage(
          {
            action: 'scheduleTask',
            tenderId: opportunity.tenderId,
            opportunityId: opportunity.id,
            opportunity: opportunity,
            targetUrl: opportunity.tender.source.url,
          },
          '*',
        );
      });
      this.$emit('download-documents-started');
    },
  },
};
</script>

<style lang="scss">
.action-manager {
  display: flex;
  position: absolute;
  justify-content: center;
  align-items: center;
  background-color: $color-white;
  border-radius: $border-radius-large;
  box-shadow: $shadow-dropdown, $shadow-default;
  font-size: $font-size-small;
  padding: 4px 12px 4px 54px;

  left: 10px;
  z-index: -1;

  @media screen and (min-width: 1200px) {
    left: 4px;
    top: 8px;
    z-index: -1;
  }

  .opportunity-status-manager .base-menu {
    min-width: 300px !important;
  }
}

.download-confirmation-dialog {
  &__warning-notice {
    margin: 32px 0 16px 0;
    font-size: 14px;
    background: $color-red-100;
    padding: 8px;
    border-radius: 5px;
    max-height: 400px;
    overflow-y: auto;

    &-header {
      display: flex;
      gap: 8px;
      font-size: 16px;
      margin-bottom: 16px;

      > span:first-of-type {
        font-weight: 700;
      }
    }

    &-text-warning {
      color: $color-error;
      font-weight: 700;
      margin-bottom: 12px;
    }

    &-opportunity {
      display: flex;
      gap: 8px;
      align-items: center;
      background-color: $color-neutral-pale;
      padding: 8px;
      margin-bottom: 8px;
    }
  }

  &__alert-icon {
    width: 24px;
    height: 24px;
    color: $color-error;
  }
}
</style>
